import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Grid, TextField, Button, Avatar, List, ListItem, ListItemAvatar, ListItemText, Divider, Tabs, Tab, IconButton, Badge, Card, CardContent, Snackbar, Alert } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import api from '../services/api';

// Custom Tab Panel component for the tabbed interface
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Friends = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  // Fetch real friend data from API
  useEffect(() => {
    const fetchFriends = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        // Try to fetch friends list - handle 404 gracefully
        try {
          const friendsResponse = await api.get('/friends');
          setFriends(friendsResponse.data);
        } catch (friendsError) {
          if (friendsError.response?.status === 404) {
            console.warn('Friends endpoint not implemented yet');
            setFriends([]);
          } else {
            throw friendsError;
          }
        }

        // Try to fetch friend requests - handle 404 gracefully
        try {
          const requestsResponse = await api.get('/friends/requests');
          setRequests(requestsResponse.data);
        } catch (requestsError) {
          if (requestsError.response?.status === 404) {
            console.warn('Friend requests endpoint not implemented yet');
            setRequests([]);
          } else {
            throw requestsError;
          }
        }
      } catch (error) {
        // Only log and show notifications for non-404 errors
        if (error.response?.status === 404) {
          console.warn('Friends endpoints not implemented yet');
        } else {
          console.error('Error fetching friends data:', error);
          showNotification('Error loading friends data', 'error');
        }
      }
    };

    fetchFriends();
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('You must be logged in to search for friends', 'error');
        return;
      }

      // Only search for exact email match
      const response = await api.get('/friends/search', {
        params: { query: searchQuery }
      });

      // Filter results for exact email match
      const exactMatches = response.data.filter(user => 
        user.email.toLowerCase() === searchQuery.toLowerCase()
      );
      
      setSearchResults(exactMatches);
      
      if (exactMatches.length === 0) {
        showNotification('No user found with that exact email', 'info');
      }
      
    } catch (error) {
      console.error('Error searching users:', error);
      showNotification('Error searching users', 'error');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSendFriendRequest = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('You must be logged in to send friend requests', 'error');
        return;
      }

      await api.post('/friends/request', { recipientId: userId });
      
      showNotification('Friend request sent successfully', 'success');
      
      // Update search results to reflect the sent request
      setSearchResults(prevResults => 
        prevResults.map(user => 
          user._id === userId 
            ? { ...user, friendshipStatus: 'pending' } 
            : user
        )
      );
    } catch (error) {
      console.error('Error sending friend request:', error);
      
      if (error.response && error.response.data) {
        showNotification(error.response.data.message, 'error');
      } else {
        showNotification('Error sending friend request', 'error');
      }
    }
  };

  const handleRespondToRequest = async (requestId, status) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('You must be logged in to respond to friend requests', 'error');
        return;
      }

      await api.patch(`/friends/respond/${requestId}`, { status });
      
      showNotification(`Friend request ${status}`, 'success');
      
      // Update UI by removing the request
      setRequests(prevRequests => prevRequests.filter(req => req._id !== requestId));
      
      // If accepted, add to friends list
      if (status === 'accepted') {
        const request = requests.find(req => req._id === requestId);
        if (request) {
          // Fetch updated friends list to ensure we have correct data
          const friendsResponse = await api.get('/friends');
          setFriends(friendsResponse.data);
        }
      }
    } catch (error) {
      console.error(`Error ${status} friend request:`, error);
      showNotification(`Error ${status} friend request`, 'error');
    }
  };

  const handleRemoveFriend = async (friendshipId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('You must be logged in to remove friends', 'error');
        return;
      }

      await api.delete(`/friends/${friendshipId}`);
      
      showNotification('Friend removed successfully', 'success');
      setFriends(prevFriends => prevFriends.filter(friend => friend.friendshipId !== friendshipId));
    } catch (error) {
      console.error('Error removing friend:', error);
      showNotification('Error removing friend', 'error');
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const showNotification = (message, severity) => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <Container>
      <Typography variant="h4" component="h1" gutterBottom>
        Friends
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="My Friends" />
          <Tab label={
            <Badge badgeContent={requests.length} color="primary">
              Requests
            </Badge>
          } />
          <Tab label="Find Friends" />
        </Tabs>
      </Box>

      {/* My Friends Tab */}
      <TabPanel value={activeTab} index={0}>
        {friends.length > 0 ? (
          <List>
            {friends.map((friend) => (
              <React.Fragment key={friend._id}>
                <ListItem
                  secondaryAction={
                    <IconButton 
                      edge="end" 
                      aria-label="remove friend"
                      onClick={() => handleRemoveFriend(friend.friendshipId)}
                    >
                      <PersonRemoveIcon />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar src={friend.profilePicture} alt={friend.name} />
                  </ListItemAvatar>
                  <ListItemText 
                    primary={friend.name} 
                    secondary={`Friends since ${new Date(friend.since).toLocaleDateString()}`} 
                  />
                </ListItem>
                <Divider variant="inset" component="li" />
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="textSecondary">
              You don't have any friends yet. Find friends in the "Find Friends" tab.
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* Friend Requests Tab */}
      <TabPanel value={activeTab} index={1}>
        {requests.length > 0 ? (
          <List>
            {requests.map((request) => (
              <React.Fragment key={request._id}>
                <ListItem
                  secondaryAction={
                    <Box>
                      <IconButton 
                        color="primary"
                        onClick={() => handleRespondToRequest(request._id, 'accepted')}
                      >
                        <CheckIcon />
                      </IconButton>
                      <IconButton 
                        color="error"
                        onClick={() => handleRespondToRequest(request._id, 'rejected')}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemAvatar>
                    <Avatar src={request.requester.profilePicture} alt={request.requester.name} />
                  </ListItemAvatar>
                  <ListItemText 
                    primary={request.requester.name} 
                    secondary={`Sent ${new Date(request.createdAt).toLocaleDateString()}`} 
                  />
                </ListItem>
                <Divider variant="inset" component="li" />
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="textSecondary">
              You don't have any pending friend requests.
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* Find Friends Tab */}
      <TabPanel value={activeTab} index={2}>
        <Box mb={4}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8} md={9}>
              <TextField
                fullWidth
                label="Search by name or email"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={isSearching || !searchQuery.trim()}
                sx={{ height: '100%' }}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={2}>
          {searchResults.map((user) => (
            <Grid item xs={12} sm={6} md={4} key={user._id}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar src={user.profilePicture} alt={user.name} sx={{ mr: 2, width: 56, height: 56 }} />
                    <Box>
                      <Typography variant="h6">{user.name}</Typography>
                      <Typography variant="body2" color="textSecondary">{user.email}</Typography>
                    </Box>
                  </Box>
                  
                  {!user.friendshipStatus && (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<PersonAddIcon />}
                      onClick={() => handleSendFriendRequest(user._id)}
                    >
                      Add Friend
                    </Button>
                  )}
                  
                  {user.friendshipStatus === 'pending' && (
                    <Button
                      fullWidth
                      variant="outlined"
                      disabled
                    >
                      Request Pending
                    </Button>
                  )}
                  
                  {user.friendshipStatus === 'accepted' && (
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      startIcon={<PersonRemoveIcon />}
                      onClick={() => handleRemoveFriend(user.friendshipId)}
                    >
                      Remove Friend
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {searchResults.length === 0 && searchQuery && !isSearching && (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="textSecondary">
              No users found matching your search.
            </Typography>
          </Box>
        )}
      </TabPanel>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Friends;